import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo, useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { triggerBilinfoSync, sendTestSyncAlert, replayLastSync, refreshBilinfoThumbnails, reimportCarImages, listBilinfoCarIdsForReimport } from "@/lib/sync.functions";
import {
  getLatestImageHealthReport,
  triggerImageHealthCheck,
  probeSingleCarImages,
  type ImageHealthReport,
  type CarImageProbeReport,
} from "@/lib/image-health.functions";
import { resolveCarSlug } from "@/lib/car-slug";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertCircle, MinusCircle, Clock, ChevronDown, Bell, History, Download, ImageOff, ExternalLink, PlayCircle, Search, Settings2, Loader2, Timer, X, Filter, Image as ImageIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Highlight } from "@/components/admin/Highlight";
import { FeedImageAuditCard } from "@/components/admin/FeedImageAuditCard";
import { FeedAltAuditCard } from "@/components/admin/FeedAltAuditCard";
import { ImageSeoAuditCard } from "@/components/admin/ImageSeoAuditCard";

const searchSchema = z.object({
  status: fallback(z.enum(["all", "success", "error", "skipped"]), "all").default("all"),
});

type SearchState = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/sync")({
  head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
  validateSearch: zodValidator(searchSchema),
  component: () => (<AdminOnlyGuard><AdminSync /></AdminOnlyGuard>),
});

type RetryAttempt = {
  bilinfoId: string;
  op: string;
  attempt: number;
  message: string;
  willRetry: boolean;
};

type FeedDiffField =
  | "price"
  | "mileage"
  | "status"
  | "is_leasing"
  | "leasing_price"
  | "image_count";

type FeedDiffExample = {
  bilinfoId: string;
  before: string | number | boolean | null;
  after: string | number | boolean | null;
  addedUrls?: string[];
  removedUrls?: string[];
  addedTotal?: number;
  removedTotal?: number;
};

type FeedDiff = {
  added: { count: number; examples: string[] };
  removed: { count: number; examples: string[] };
  changed: Record<FeedDiffField, { count: number; examples: FeedDiffExample[] }>;
  unchanged: number;
  totalCompared: number;
};

type SyncLogMetadata = {
  failedStep?: string;
  lastStep?: string;
  recentCarIds?: string[];
  itemErrors?: Array<{ bilinfoId: string; op: string; message: string; attempts?: number }>;
  itemErrorCount?: number;
  retries?: RetryAttempt[];
  retryAttemptCount?: number;
  retrySuccessCount?: number;
  feedDiff?: FeedDiff | null;
  stack?: string | null;
};

type SyncLog = {
  id: string;
  source: string;
  status: string;
  cars_created: number;
  cars_updated: number;
  cars_marked_sold: number;
  cars_in_feed: number;
  duration_ms: number | null;
  error_message: string | null;
  triggered_by: string;
  created_at: string;
  metadata: SyncLogMetadata | null;
};

type SyncAlertRow = {
  id: string;
  message_id: string | null;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
  metadata: unknown;
};

type CarSlugInfo = { slug: string; status: string | null };
type CarSlugMap = Record<string, CarSlugInfo>;

const CarSlugContext = createContext<CarSlugMap>({});

function CarIdLink({ bilinfoId, className }: { bilinfoId: string; className?: string }) {
  const map = useContext(CarSlugContext);
  const info = map[bilinfoId];
  const baseClass = className ?? "text-foreground";
  if (!info) {
    return (
      <code
        className={baseClass}
        title="Bilen findes ikke længere i databasen"
      >
        {bilinfoId}
      </code>
    );
  }
  return (
    <Link
      to="/biler-til-salg/$slug"
      params={{ slug: info.slug }}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseClass} underline-offset-2 hover:underline`}
      title={`Åbn bil ${bilinfoId}${info.status ? ` (${info.status})` : ""}`}
    >
      {bilinfoId}
    </Link>
  );
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari can read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvEscape(value: unknown): string {
  if (value == null) return "";
  const s = typeof value === "string" ? value : JSON.stringify(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildErrorCsv(logs: SyncLog[]): string {
  const headers = [
    "log_id",
    "log_created_at",
    "triggered_by",
    "duration_ms",
    "log_error_message",
    "failed_step",
    "last_step",
    "cars_in_feed",
    "cars_created",
    "cars_updated",
    "cars_marked_sold",
    "item_bilinfo_id",
    "item_op",
    "item_attempts",
    "item_error_message",
  ];
  const rows: string[] = [headers.join(",")];
  for (const log of logs) {
    const meta = log.metadata ?? null;
    const failedStep = meta?.failedStep ?? "";
    const lastStep = meta?.lastStep ?? "";
    const items = meta?.itemErrors ?? [];
    const baseCols = [
      log.id,
      log.created_at,
      log.triggered_by,
      log.duration_ms ?? "",
      log.error_message ?? "",
      failedStep,
      lastStep,
      log.cars_in_feed,
      log.cars_created,
      log.cars_updated,
      log.cars_marked_sold,
    ];
    if (items.length === 0) {
      rows.push([...baseCols, "", "", "", ""].map(csvEscape).join(","));
    } else {
      for (const it of items) {
        rows.push(
          [...baseCols, it.bilinfoId, it.op, it.attempts ?? "", it.message]
            .map(csvEscape)
            .join(","),
        );
      }
    }
  }
  return rows.join("\n");
}

/**
 * Detekter 401/Unauthorized i sync-fejl og giv en konkret guide
 * til hvilken credential der mangler og hvordan den opdateres.
 */
function diagnoseSyncError(detail: string, step?: string): { credential: string; where: string } | null {
  const text = `${detail} ${step ?? ""}`.toLowerCase();
  const is401 =
    text.includes("401") ||
    text.includes("unauthorized") ||
    text.includes("forbidden") ||
    text.includes("403");
  if (!is401) return null;

  // Bilinfo feed/auth fejl
  if (
    step === "fetch-feed" ||
    text.includes("bilinfo") ||
    text.includes("feed")
  ) {
    return {
      credential: "BILINFO_USERNAME / BILINFO_PASSWORD",
      where:
        "Opdater dine Bilinfo-credentials under Project Settings → Secrets (Lovable Cloud). Tjek at brugernavn og password er korrekte, og at kontoen stadig har adgang til feedet.",
    };
  }

  // Supabase service role fejl
  if (
    text.includes("service_role") ||
    text.includes("service role") ||
    text.includes("admin_service_role") ||
    text.includes("supabase")
  ) {
    return {
      credential: "ADMIN_SERVICE_ROLE_KEY / SUPABASE_SERVICE_ROLE_KEY",
      where:
        "Rotér og opdater service role-nøglen under Project Settings → Secrets. Den bruges af sync til at skrive til databasen.",
    };
  }

  // Generel fallback
  return {
    credential: "Ukendt API-nøgle",
    where:
      "Tjek de relevante secrets under Project Settings → Secrets (Lovable Cloud). Mest sandsynlige kandidater: BILINFO_USERNAME, BILINFO_PASSWORD, ADMIN_SERVICE_ROLE_KEY.",
  };
}

function AdminSync() {
  const queryClient = useQueryClient();
  const triggerSync = useServerFn(triggerBilinfoSync);
  const sendTestAlert = useServerFn(sendTestSyncAlert);
  const replaySync = useServerFn(replayLastSync);
  const refreshThumbs = useServerFn(refreshBilinfoThumbnails);
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/sync" });
  const setStatus = (status: SearchState["status"]) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, status }), replace: true });
  };

  const { data: logs, isLoading } = useQuery({
    queryKey: ["sync-logs"],
    queryFn: async (): Promise<SyncLog[]> => {
      const { data, error } = await supabase
        .from("sync_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as SyncLog[];
    },
    refetchInterval: 15000,
  });

  const { data: bilinfoCount } = useQuery({
    queryKey: ["bilinfo-car-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("cars")
        .select("*", { count: "exact", head: true })
        .eq("source", "bilinfo");
      return count ?? 0;
    },
  });

  const { data: carSlugMap } = useQuery({
    queryKey: ["bilinfo-car-slug-map"],
    queryFn: async (): Promise<CarSlugMap> => {
      const { data, error } = await supabase
        .from("cars")
        .select("id, bilinfo_id, seo_slug, status")
        .eq("source", "bilinfo")
        .not("bilinfo_id", "is", null);
      if (error) throw error;
      const map: CarSlugMap = {};
      for (const row of data ?? []) {
        if (!row.bilinfo_id) continue;
        map[row.bilinfo_id] = {
          slug: resolveCarSlug(row),
          status: row.status ?? null,
        };
      }
      return map;
    },
    staleTime: 60_000,
  });

  const { data: recentAlerts, isLoading: alertsLoading } = useQuery({
    queryKey: ["recent-sync-alerts"],
    queryFn: async (): Promise<SyncAlertRow[]> => {
      const { data, error } = await supabase
        .from("email_send_log")
        .select("id, message_id, recipient_email, status, error_message, created_at, metadata")
        .eq("template_name", "admin-sync-failure-alert")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      // Dedupe by message_id, keeping the latest row per email
      const seen = new Set<string>();
      const deduped: SyncAlertRow[] = [];
      for (const row of (data ?? []) as SyncAlertRow[]) {
        const key = row.message_id ?? row.id;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(row);
        if (deduped.length >= 5) break;
      }
      return deduped;
    },
    refetchInterval: 30_000,
  });

  const lastSuccess = logs?.find((l) => l.status === "success");
  const errorLogs = useMemo(() => (logs ?? []).filter((l) => l.status === "error"), [logs]);
  const latestError = errorLogs[0];

  const statusCounts = useMemo(() => {
    const list = logs ?? [];
    return {
      all: list.length,
      success: list.filter((l) => l.status === "success").length,
      error: list.filter((l) => l.status === "error").length,
      skipped: list.filter((l) => l.status === "skipped").length,
    };
  }, [logs]);

  const filteredLogs = useMemo(() => {
    if (!logs) return logs;
    if (urlSearch.status === "all") return logs;
    return logs.filter((l) => l.status === urlSearch.status);
  }, [logs, urlSearch.status]);

  const syncMutation = useMutation({
    mutationFn: async (mode: "full" | "delta") => triggerSync({ data: { mode } }),
    onSuccess: (res) => {
      const payload = res.payload as
        | { status?: string; reason?: string; message?: string; failedStep?: string }
        | undefined;
      const modeLabel = res.mode === "delta" ? "Delta-sync" : "Fuld sync";
      if (res.ok && payload?.status === "success") {
        toast.success(`${modeLabel} færdig`);
      } else if (payload?.status === "skipped") {
        toast.warning(`Sprunget over: ${payload.reason ?? "ukendt grund"}`);
      } else {
        const detail =
          payload?.message ??
          payload?.reason ??
          `HTTP ${res.status}`;
        const step = payload?.failedStep ? ` [${payload.failedStep}]` : "";
        const guide = diagnoseSyncError(detail, payload?.failedStep);
        if (guide) {
          toast.error(`${modeLabel} fejlede${step}: 401 Unauthorized`, {
            description: `Manglende/ugyldig credential: ${guide.credential}\n\n${guide.where}\n\nFejldetalje: ${detail}`,
            duration: 20000,
          });
        } else {
          toast.error(`${modeLabel} fejlede${step}: ${detail}`, { duration: 12000 });
        }
        console.error("[sync] full sync failed", res);
      }
      queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      queryClient.invalidateQueries({ queryKey: ["bilinfo-car-count"] });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "Kunne ikke starte sync";
      const guide = diagnoseSyncError(msg);
      if (guide) {
        toast.error("Sync fejlede: 401 Unauthorized", {
          description: `Manglende/ugyldig credential: ${guide.credential}\n\n${guide.where}\n\nFejldetalje: ${msg}`,
          duration: 20000,
        });
      } else {
        toast.error(msg, { duration: 12000 });
      }
      console.error("[sync] mutation error", e);
    },
  });

  const testAlertMutation = useMutation({
    mutationFn: async (variant: "error" | "anomaly" | "recurring") =>
      sendTestAlert({ data: { variant } }),
    onSuccess: (res) => {
      const label =
        res.variant === "error"
          ? "Fejl-alarm"
          : res.variant === "anomaly"
            ? "Anomaly-alarm"
            : "Gentagne-fejl-alarm";
      toast.success(`${label} sendt til ${res.recipient}`);
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Kunne ikke sende test-alarm");
    },
  });

  const replayMutation = useMutation({
    mutationFn: async () => replaySync(),
    onSuccess: (res) => {
      const modeLabel = res.mode === "delta" ? "Delta-sync" : "Fuld sync";
      const sinceLabel = res.sinceDays != null ? ` (sidste ${res.sinceDays} dage)` : "";
      const payload = res.payload;
      if (res.ok && payload?.status === "success") {
        toast.success(`Genkørt: ${modeLabel}${sinceLabel}`);
      } else if (payload?.status === "skipped") {
        toast.warning(`Sprunget over: ${payload.reason ?? "ukendt grund"}`);
      } else {
        const payloadAny = payload as { message?: string; reason?: string; failedStep?: string } | undefined;
        const detail = payloadAny?.message ?? payloadAny?.reason ?? "ukendt fejl";
        const guide = diagnoseSyncError(detail, payloadAny?.failedStep);
        if (guide) {
          toast.error(`Genkørsel af ${modeLabel}${sinceLabel} fejlede: 401 Unauthorized`, {
            description: `Manglende/ugyldig credential: ${guide.credential}\n\n${guide.where}\n\nFejldetalje: ${detail}`,
            duration: 20000,
          });
        } else {
          toast.error(`Genkørsel af ${modeLabel}${sinceLabel} fejlede: ${detail}`, { duration: 12000 });
        }
      }
      queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      queryClient.invalidateQueries({ queryKey: ["bilinfo-car-count"] });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Kunne ikke gentage sidste sync");
    },
  });

  const refreshThumbsMutation = useMutation({
    mutationFn: async (scope: "all" | "changed") => refreshThumbs({ data: { scope } }),
    onSuccess: (res) => {
      const scopeLabel = res.scope === "all" ? "alle biler" : "ændrede biler";
      const payload = res.payload;
      if (res.ok && payload?.status === "success") {
        const updated = payload.updated ?? 0;
        toast.success(`Thumbnails gen-downloadet for ${scopeLabel} (${updated} opdateret)`);
      } else if (payload?.status === "skipped") {
        toast.warning(`Sprunget over: ${payload.reason ?? "ukendt grund"}`);
      } else {
        const payloadAny = payload as { message?: string; reason?: string; failedStep?: string } | undefined;
        const detail = payloadAny?.message ?? payloadAny?.reason ?? "ukendt fejl";
        const guide = diagnoseSyncError(detail, payloadAny?.failedStep);
        if (guide) {
          toast.error("Gen-download af thumbnails fejlede: 401 Unauthorized", {
            description: `Manglende/ugyldig credential: ${guide.credential}\n\n${guide.where}\n\nFejldetalje: ${detail}`,
            duration: 20000,
          });
        } else {
          toast.error(`Gen-download af thumbnails fejlede: ${detail}`, { duration: 12000 });
        }
      }
      queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      queryClient.invalidateQueries({ queryKey: ["bilinfo-car-count"] });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Kunne ikke gen-downloade thumbnails");
    },
  });

  const exportErrors = (scope: "latest" | "all", format: "json" | "csv") => {
    const source = scope === "latest" ? (latestError ? [latestError] : []) : errorLogs;
    if (source.length === 0) {
      toast.warning("Ingen fejl-kørsler at eksportere");
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const baseName =
      scope === "latest"
        ? `sync-errors-latest-${stamp}`
        : `sync-errors-all-${source.length}-${stamp}`;
    if (format === "json") {
      const payload = source.map((log) => ({
        id: log.id,
        source: log.source,
        status: log.status,
        triggered_by: log.triggered_by,
        created_at: log.created_at,
        duration_ms: log.duration_ms,
        cars_created: log.cars_created,
        cars_updated: log.cars_updated,
        cars_marked_sold: log.cars_marked_sold,
        cars_in_feed: log.cars_in_feed,
        error_message: log.error_message,
        metadata: log.metadata,
      }));
      downloadFile(
        JSON.stringify(payload, null, 2),
        `${baseName}.json`,
        "application/json",
      );
    } else {
      downloadFile(buildErrorCsv(source), `${baseName}.csv`, "text/csv;charset=utf-8");
    }
    toast.success(
      `${source.length} fejl-kørsel${source.length === 1 ? "" : "er"} eksporteret som ${format.toUpperCase()}`,
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bilinfo-synkronisering</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Fuld import 1× dagligt (kl. 03:00) + delta-opdatering hver time. Manuelle biler røres ikke.
          </p>
          <NextCronBadge />
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={errorLogs.length === 0} className="gap-2">
                <Download size={16} />
                Eksportér fejl
                {errorLogs.length > 0 && (
                  <span className="rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-destructive">
                    {errorLogs.length}
                  </span>
                )}
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuItem
                onClick={() => exportErrors("latest", "json")}
                disabled={!latestError}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Seneste fejl — JSON</span>
                <span className="text-xs text-muted-foreground">
                  Fuld metadata + per-bil fejl fra sidste fejl-kørsel.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportErrors("latest", "csv")}
                disabled={!latestError}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Seneste fejl — CSV</span>
                <span className="text-xs text-muted-foreground">
                  Én række per bil-fejl, åbner direkte i Excel.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportErrors("all", "json")}
                disabled={errorLogs.length === 0}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Alle fejl-kørsler — JSON</span>
                <span className="text-xs text-muted-foreground">
                  {errorLogs.length} kørsel{errorLogs.length === 1 ? "" : "er"} fra de seneste 50 logs.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportErrors("all", "csv")}
                disabled={errorLogs.length === 0}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Alle fejl-kørsler — CSV</span>
                <span className="text-xs text-muted-foreground">
                  Flad tabel med alle per-bil fejl på tværs af kørsler.
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={testAlertMutation.isPending}
                className="gap-2"
              >
                <Bell size={16} className={testAlertMutation.isPending ? "animate-pulse" : ""} />
                {testAlertMutation.isPending ? "Sender..." : "Test-alarm"}
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuItem
                onClick={() => testAlertMutation.mutate("error")}
                disabled={testAlertMutation.isPending}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Fejl-alarm</span>
                <span className="text-xs text-muted-foreground">
                  Simulerer at sync-jobbet fejlede med en API-fejl.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => testAlertMutation.mutate("anomaly")}
                disabled={testAlertMutation.isPending}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Anomaly-alarm</span>
                <span className="text-xs text-muted-foreground">
                  Simulerer pludseligt fald: 12 → 5 biler (-58%).
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => testAlertMutation.mutate("recurring")}
                disabled={testAlertMutation.isPending}
                className="flex-col items-start gap-0.5"
              >
                <span className="font-medium">Gentagne-fejl-alarm</span>
                <span className="text-xs text-muted-foreground">
                  Simulerer samme trin (fetch-feed) fejlet 4 af seneste 5 kørsler.
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-stretch overflow-hidden rounded-md ring-1 ring-accent/30">
            <Button
              onClick={() => syncMutation.mutate("full")}
              disabled={syncMutation.isPending}
              className="rounded-none border-0 bg-accent text-accent-foreground hover:brightness-110"
            >
              <RefreshCw size={16} className={syncMutation.isPending ? "animate-spin" : ""} />
              {syncMutation.isPending ? "Synkroniserer..." : "Synkronisér nu"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={syncMutation.isPending}
                  aria-label="Vælg sync-type"
                  className="rounded-none border-0 border-l border-accent-foreground/20 bg-accent px-2 text-accent-foreground hover:brightness-110"
                >
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem
                  onClick={() => syncMutation.mutate("full")}
                  disabled={syncMutation.isPending}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="font-medium">Fuld sync</span>
                  <span className="text-xs text-muted-foreground">
                    Henter alle biler. Markerer manglende som solgt.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => syncMutation.mutate("delta")}
                  disabled={syncMutation.isPending}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="font-medium">Delta-sync (sidste 2 dage)</span>
                  <span className="text-xs text-muted-foreground">
                    Hurtigere — kun ændringer siden sidst.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => replayMutation.mutate()}
                  disabled={syncMutation.isPending || replayMutation.isPending || !lastSuccess}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="flex items-center gap-1.5 font-medium">
                    <History size={14} />
                    {replayMutation.isPending ? "Gentager..." : "Gentag sidste sync"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {lastSuccess
                      ? "Bruger samme mode + sinceDays som seneste succes."
                      : "Ingen tidligere succes at gentage."}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => refreshThumbsMutation.mutate("changed")}
                  disabled={syncMutation.isPending || refreshThumbsMutation.isPending}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="flex items-center gap-1.5 font-medium">
                    <ImageIcon size={14} />
                    {refreshThumbsMutation.isPending && refreshThumbsMutation.variables === "changed"
                      ? "Henter..."
                      : "Gen-download ændrede thumbnails"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Delta-sync — henter kun primær-billeder hvor kilde-URL er ændret.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => refreshThumbsMutation.mutate("all")}
                  disabled={syncMutation.isPending || refreshThumbsMutation.isPending}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="flex items-center gap-1.5 font-medium">
                    <ImageIcon size={14} />
                    {refreshThumbsMutation.isPending && refreshThumbsMutation.variables === "all"
                      ? "Henter..."
                      : "Tving gen-download af ALLE thumbnails"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Fuld sync — re-uploader hver bils primær-billede uanset hash. Tager længere tid.
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Status overview */}
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Biler fra Bilinfo"
          value={bilinfoCount ?? 0}
          icon={<RefreshCw size={18} />}
        />
        <StatCard
          label="Sidste succes"
          value={lastSuccess ? formatRelative(lastSuccess.created_at) : "Ingen endnu"}
          icon={<Clock size={18} />}
          small
        />
        <StatCard
          label="Status"
          value={
            !lastSuccess
              ? "Venter på første kørsel"
              : `${lastSuccess.cars_in_feed} biler i sidste feed`
          }
          icon={<CheckCircle2 size={18} />}
          small
        />
      </div>

      {/* Re-import ALL images from Bilinfo (per car) */}
      <ReimportAllImagesCard />

      {/* Recent sync alerts widget */}
      <div className="mt-8 rounded-xl bg-card ring-1 ring-border">
        <div className="flex items-center justify-between gap-3 border-b border-border p-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Seneste sync-alarmer</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              De sidste 5 fejl- og anomaly-emails sendt til admins.
            </p>
          </div>
          <Bell size={18} className="text-muted-foreground" />
        </div>
        {alertsLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Indlæser...</div>
        ) : !recentAlerts || recentAlerts.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Ingen alarmer sendt endnu.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tidspunkt</TableHead>
                <TableHead>Modtager</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentAlerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(alert.created_at).toLocaleString("da-DK")}
                  </TableCell>
                  <TableCell className="text-sm">{alert.recipient_email}</TableCell>
                  <TableCell>
                    <AlertStatusBadge status={alert.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Image health report (post-sync sample) */}
      <ImageHealthCard />

      {/* Ad-feed image audit (full feed inventory) */}
      <FeedImageAuditCard />

      {/* Ad-feed image-alt audit — descriptive + unique alt for every image */}
      <FeedAltAuditCard />

      {/* Combined image-SEO audit — alt + size + format pr. car */}
      <ImageSeoAuditCard />

      {/* Setup notice if no successful run yet */}
      {!isLoading && (!logs || logs.length === 0) && (
        <div className="mt-6 rounded-xl bg-card p-6 ring-1 ring-border">
          <h3 className="font-semibold text-foreground">Kom i gang</h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Bilinfo-credentials er gemt som <code>BILINFO_USERNAME</code> + <code>BILINFO_PASSWORD</code></li>
            <li>Tryk "Synkronisér nu" for at hente alle biler første gang</li>
            <li>Cron-jobbet kører automatisk dagligt (fuld) og hver time (delta) bagefter</li>
          </ol>
        </div>
      )}

      {/* Sync log */}
      <div className="mt-8 rounded-xl bg-card ring-1 ring-border">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-6">
          <h2 className="text-lg font-semibold text-foreground">Seneste kørsler</h2>
          <div className="flex gap-1 rounded-md border border-border p-1">
            {(["all", "success", "error", "skipped"] as const).map((s) => {
              const count = statusCounts[s];
              const isActive = urlSearch.status === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition ${
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {s === "all" ? "Alle" : s === "success" ? "Succes" : s === "error" ? "Fejl" : "Sprunget"}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                      isActive
                        ? "bg-accent-foreground/15 text-accent-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Indlæser...</div>
        ) : !filteredLogs || filteredLogs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {!logs || logs.length === 0
              ? "Ingen synkroniseringer endnu"
              : `Ingen kørsler matcher "${urlSearch.status === "success" ? "Succes" : urlSearch.status === "error" ? "Fejl" : "Sprunget"}"`}
          </div>
        ) : (
          <CarSlugContext.Provider value={carSlugMap ?? {}}>
            <div className="divide-y divide-border">
              {filteredLogs.map((log) => (
                <LogRow key={log.id} log={log} />
              ))}
            </div>
          </CarSlugContext.Provider>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  small,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  small?: boolean;
}) {
  return (
    <div className="rounded-xl bg-card p-5 ring-1 ring-border">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`mt-2 font-semibold text-foreground ${small ? "text-base" : "text-3xl"}`}>
        {value}
      </p>
    </div>
  );
}

function LogRow({ log }: { log: SyncLog }) {
  const StatusIcon =
    log.status === "success"
      ? CheckCircle2
      : log.status === "skipped"
        ? MinusCircle
        : AlertCircle;
  const statusColor =
    log.status === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : log.status === "skipped"
        ? "text-muted-foreground"
        : "text-destructive";

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <StatusIcon size={20} className={`mt-0.5 shrink-0 ${statusColor}`} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium capitalize text-foreground">{log.status}</span>
              <Badge variant="outline" className="text-xs">
                {log.triggered_by}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {new Date(log.created_at).toLocaleString("da-DK")}
              {log.duration_ms != null && ` · ${(log.duration_ms / 1000).toFixed(1)}s`}
            </p>
            {log.status === "success" && (
              <p className="mt-2 text-sm text-muted-foreground">
                {log.cars_in_feed} i feed · <span className="font-medium text-foreground">{log.cars_created} oprettet</span>{" "}
                · <span className="font-medium text-foreground">{log.cars_updated} opdateret</span>
                {log.cars_marked_sold > 0 && (
                  <>
                    {" "}· <span className="font-medium text-foreground">{log.cars_marked_sold} markeret solgt</span>
                  </>
                )}
                {(log.metadata?.retryAttemptCount ?? 0) > 0 && (
                  <>
                    {" "}·{" "}
                    <span className="font-medium text-foreground">
                      {log.metadata?.retryAttemptCount} retry-forsøg
                    </span>
                    {(log.metadata?.retrySuccessCount ?? 0) > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {" "}({log.metadata?.retrySuccessCount} reddet)
                      </span>
                    )}
                  </>
                )}
              </p>
            )}
            {log.error_message && (
              <p className="mt-2 break-all rounded bg-destructive/10 p-2 text-xs text-destructive">
                {log.error_message}
              </p>
            )}
            {log.metadata && (
              log.status === "error" ||
              (log.metadata.itemErrorCount ?? 0) > 0 ||
              (log.metadata.retryAttemptCount ?? 0) > 0
            ) && <SyncErrorDetails metadata={log.metadata} />}
            {log.metadata?.feedDiff && (
              <FeedDiffSection diff={log.metadata.feedDiff} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SyncErrorDetails({ metadata }: { metadata: SyncLogMetadata }) {
  const failedStep = metadata.failedStep ?? metadata.lastStep;
  const recentIds = metadata.recentCarIds ?? [];
  const itemErrors = metadata.itemErrors ?? [];
  const itemErrorCount = metadata.itemErrorCount ?? itemErrors.length;
  const retries = metadata.retries ?? [];
  const retryAttemptCount = metadata.retryAttemptCount ?? retries.length;
  const retrySuccessCount = metadata.retrySuccessCount ?? 0;
  if (
    !failedStep &&
    recentIds.length === 0 &&
    itemErrors.length === 0 &&
    retries.length === 0
  )
    return null;
  return (
    <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/40 p-3 text-xs">
      {failedStep && (
        <div>
          <span className="font-semibold text-foreground">Fejlende trin:</span>{" "}
          <code className="rounded bg-background px-1.5 py-0.5 text-foreground">{failedStep}</code>
        </div>
      )}
      {retryAttemptCount > 0 && (
        <div>
          <span className="font-semibold text-foreground">Retries:</span>{" "}
          <span className="text-muted-foreground">
            {retryAttemptCount} forsøg
            {retrySuccessCount > 0 && (
              <>
                {" "}·{" "}
                <span className="text-emerald-600 dark:text-emerald-400">
                  {retrySuccessCount} lykkedes efter retry
                </span>
              </>
            )}
            {retries.length < retryAttemptCount && (
              <> · viser {retries.length} seneste</>
            )}
          </span>
          <ul className="mt-1 space-y-1">
            {retries.map((r, idx) => (
              <li
                key={`${r.bilinfoId}-${r.attempt}-${idx}`}
                className="rounded bg-background px-2 py-1 text-muted-foreground"
              >
                <code className="text-foreground">{r.bilinfoId}</code>{" "}
                <Badge variant="outline" className="ml-1 text-[10px]">{r.op}</Badge>
                <Badge
                  variant={r.willRetry ? "secondary" : "destructive"}
                  className="ml-1 text-[10px]"
                >
                  forsøg {r.attempt}{r.willRetry ? " · prøver igen" : " · opgivet"}
                </Badge>
                <div className="mt-0.5 break-all">{r.message}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {recentIds.length > 0 && (
        <div>
          <span className="font-semibold text-foreground">
            Sidste {recentIds.length} bil-ID{recentIds.length === 1 ? "" : "er"} (nyeste sidst):
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {recentIds.map((id, idx) => (
              <code
                key={`${id}-${idx}`}
                className={`rounded px-1.5 py-0.5 text-[11px] ${
                  idx === recentIds.length - 1
                    ? "bg-destructive/15 text-destructive"
                    : "bg-background text-muted-foreground"
                }`}
              >
                {id}
              </code>
            ))}
          </div>
        </div>
      )}
      {itemErrors.length > 0 && (
        <div>
          <span className="font-semibold text-foreground">
            Pr.-bil fejl ({itemErrorCount}{itemErrorCount > itemErrors.length ? `, viser ${itemErrors.length} seneste` : ""}):
          </span>
          <ul className="mt-1 space-y-1">
            {itemErrors.map((err, idx) => (
              <li
                key={`${err.bilinfoId}-${idx}`}
                className="rounded bg-background px-2 py-1 text-muted-foreground"
              >
                <code className="text-foreground">{err.bilinfoId}</code>{" "}
                <Badge variant="outline" className="ml-1 text-[10px]">{err.op}</Badge>
                {err.attempts != null && err.attempts > 1 && (
                  <Badge variant="outline" className="ml-1 text-[10px]">
                    {err.attempts} forsøg
                  </Badge>
                )}
                <div className="mt-0.5 break-all">{err.message}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const FEED_DIFF_FIELD_LABELS: Record<FeedDiffField, string> = {
  price: "Pris",
  mileage: "Km-stand",
  status: "Status",
  is_leasing: "Leasing-flag",
  leasing_price: "Leasing-pris",
  image_count: "Antal billeder",
};

function formatDiffValue(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "ja" : "nej";
  if (typeof v === "number") return v.toLocaleString("da-DK");
  return v;
}

type DiffFilterKey = "price" | "mileage" | "leasing" | "images" | "status";

const DIFF_FILTER_LABELS: Record<DiffFilterKey, string> = {
  price: "Pris",
  mileage: "Km-stand",
  leasing: "Leasing",
  images: "Billeder",
  status: "Status",
};

const DIFF_FILTER_FIELDS: Record<DiffFilterKey, FeedDiffField[]> = {
  price: ["price"],
  mileage: ["mileage"],
  leasing: ["is_leasing", "leasing_price"],
  images: ["image_count"],
  status: ["status"],
};

const DIFF_FILTER_STORAGE_KEY = "admin-sync.feed-diff-filters";
const DEFAULT_DIFF_FILTERS: Record<DiffFilterKey, boolean> = {
  price: true,
  mileage: true,
  leasing: true,
  images: true,
  status: true,
};

function FeedDiffSection({ diff }: { diff: FeedDiff }) {
  const [filters, setFilters] = useState<Record<DiffFilterKey, boolean>>(DEFAULT_DIFF_FILTERS);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(DIFF_FILTER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<Record<DiffFilterKey, boolean>>;
      setFilters((prev) => ({ ...prev, ...parsed }));
    } catch {
      // ignore
    }
  }, []);

  const updateFilter = (key: DiffFilterKey, value: boolean) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(DIFF_FILTER_STORAGE_KEY, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
      return next;
    });
  };

  const visibleFields: FeedDiffField[] = (Object.keys(filters) as DiffFilterKey[])
    .filter((k) => filters[k])
    .flatMap((k) => DIFF_FILTER_FIELDS[k]);

  const allChangedFields = (Object.keys(diff.changed) as FeedDiffField[]).filter(
    (f) => diff.changed[f].count > 0,
  );
  const changedFields = allChangedFields.filter((f) => visibleFields.includes(f));
  const hiddenChangedCount = allChangedFields.length - changedFields.length;
  const totalChangedCars =
    diff.totalCompared > 0 ? diff.totalCompared - diff.unchanged : 0;
  const hasAnything =
    diff.added.count > 0 ||
    diff.removed.count > 0 ||
    allChangedFields.length > 0 ||
    diff.totalCompared > 0;
  if (!hasAnything) return null;

  return (
    <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-foreground">Feed-diff vs. forrige tilstand</span>
        <span className="text-muted-foreground">
          {diff.totalCompared} sammenlignet · {diff.unchanged} uændret
        </span>
      </div>

      {/* Field toggles */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded border border-border/60 bg-background/60 px-2 py-1.5">
        <span className="text-[11px] font-medium text-muted-foreground">Vis felter:</span>
        {(Object.keys(DIFF_FILTER_LABELS) as DiffFilterKey[]).map((key) => {
          const id = `feed-diff-filter-${key}`;
          return (
            <div key={key} className="flex items-center gap-1.5">
              <Checkbox
                id={id}
                checked={filters[key]}
                onCheckedChange={(v) => updateFilter(key, v === true)}
              />
              <Label htmlFor={id} className="cursor-pointer text-[11px] font-normal">
                {DIFF_FILTER_LABELS[key]}
              </Label>
            </div>
          );
        })}
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-1.5">
        <DiffChip label="Tilføjet" count={diff.added.count} tone="add" />
        <DiffChip label="Fjernet" count={diff.removed.count} tone="remove" />
        <DiffChip label="Ændret" count={totalChangedCars} tone="change" />
      </div>

      {/* Added examples */}
      {diff.added.count > 0 && (
        <div>
          <div className="text-muted-foreground">
            <span className="font-medium text-foreground">Nye biler ({diff.added.count}):</span>{" "}
            {diff.added.examples.length < diff.added.count && (
              <span>viser {diff.added.examples.length} eksempler</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {diff.added.examples.map((id) => (
              <span
                key={id}
                className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400"
              >
                <CarIdLink bilinfoId={id} className="text-emerald-700 dark:text-emerald-400" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Removed examples */}
      {diff.removed.count > 0 && (
        <div>
          <div className="text-muted-foreground">
            <span className="font-medium text-foreground">
              Fjernet/markeres solgt ({diff.removed.count}):
            </span>{" "}
            {diff.removed.examples.length < diff.removed.count && (
              <span>viser {diff.removed.examples.length} eksempler</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {diff.removed.examples.map((id) => (
              <span
                key={id}
                className="rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] text-destructive"
              >
                <CarIdLink bilinfoId={id} className="text-destructive" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-field changes */}
      {changedFields.length > 0 && (
        <div>
          <div className="font-medium text-foreground">Felt-ændringer:</div>
          <ul className="mt-1 space-y-1.5">
            {changedFields.map((field) => {
              const f = diff.changed[field];
              return (
                <li key={field} className="rounded bg-background px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-foreground">
                      {FEED_DIFF_FIELD_LABELS[field]}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {f.count} {f.count === 1 ? "bil" : "biler"}
                    </Badge>
                  </div>
                  <ul className="mt-1 space-y-1">
                    {f.examples.map((ex, idx) => {
                      const hasUrlDiff =
                        field === "image_count" &&
                        ((ex.addedUrls && ex.addedUrls.length > 0) ||
                          (ex.removedUrls && ex.removedUrls.length > 0));
                      return (
                        <li
                          key={`${ex.bilinfoId}-${idx}`}
                          className="text-muted-foreground"
                        >
                          <div className="flex flex-wrap items-center gap-1.5">
                            <CarIdLink bilinfoId={ex.bilinfoId} />
                            <span className="text-muted-foreground">
                              {formatDiffValue(ex.before)}
                            </span>
                            <span aria-hidden>→</span>
                            <span className="font-medium text-foreground">
                              {formatDiffValue(ex.after)}
                            </span>
                            {hasUrlDiff && (
                              <span className="text-[10px] text-muted-foreground">
                                {(ex.addedTotal ?? 0) > 0 && (
                                  <span className="text-emerald-600 dark:text-emerald-400">
                                    +{ex.addedTotal}
                                  </span>
                                )}
                                {(ex.addedTotal ?? 0) > 0 && (ex.removedTotal ?? 0) > 0 && " / "}
                                {(ex.removedTotal ?? 0) > 0 && (
                                  <span className="text-destructive">
                                    −{ex.removedTotal}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {hasUrlDiff && (
                            <ImageUrlDiff
                              addedUrls={ex.addedUrls ?? []}
                              removedUrls={ex.removedUrls ?? []}
                              addedTotal={ex.addedTotal ?? 0}
                              removedTotal={ex.removedTotal ?? 0}
                            />
                          )}
                        </li>
                      );
                    })}
                    {f.count > f.examples.length && (
                      <li className="text-[11px] text-muted-foreground">
                        … +{f.count - f.examples.length} flere
                      </li>
                    )}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hiddenChangedCount > 0 && (
        <div className="text-[11px] italic text-muted-foreground">
          {hiddenChangedCount} {hiddenChangedCount === 1 ? "felt-ændring" : "felt-ændringer"} skjult af filtre.
        </div>
      )}
    </div>
  );
}

function ImageUrlDiff({
  addedUrls,
  removedUrls,
  addedTotal,
  removedTotal,
}: {
  addedUrls: string[];
  removedUrls: string[];
  addedTotal: number;
  removedTotal: number;
}) {
  const fileName = (url: string): string => {
    try {
      const u = new URL(url);
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last || url;
    } catch {
      const last = url.split("/").filter(Boolean).pop();
      return last || url;
    }
  };

  if (addedUrls.length === 0 && removedUrls.length === 0) return null;
  return (
    <div className="mt-1 ml-4 space-y-0.5 border-l border-border/60 pl-2 text-[11px]">
      {addedUrls.length > 0 && (
        <div>
          <span className="font-medium text-emerald-700 dark:text-emerald-400">
            + tilføjet
          </span>
          <ul className="mt-0.5 space-y-0.5">
            {addedUrls.map((url) => (
              <li key={`a-${url}`} className="flex items-center gap-1.5">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-400"
                  title={url}
                >
                  {fileName(url)}
                </a>
              </li>
            ))}
            {addedTotal > addedUrls.length && (
              <li className="text-muted-foreground">
                … +{addedTotal - addedUrls.length} flere
              </li>
            )}
          </ul>
        </div>
      )}
      {removedUrls.length > 0 && (
        <div>
          <span className="font-medium text-destructive">− fjernet</span>
          <ul className="mt-0.5 space-y-0.5">
            {removedUrls.map((url) => (
              <li key={`r-${url}`} className="flex items-center gap-1.5">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-destructive underline-offset-2 hover:underline"
                  title={url}
                >
                  {fileName(url)}
                </a>
              </li>
            ))}
            {removedTotal > removedUrls.length && (
              <li className="text-muted-foreground">
                … +{removedTotal - removedUrls.length} flere
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function DiffChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "add" | "remove" | "change";
}) {
  const toneClass =
    tone === "add"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-500/20"
      : tone === "remove"
        ? "bg-destructive/10 text-destructive ring-destructive/20"
        : "bg-accent/10 text-foreground ring-accent/30";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${toneClass}`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{count}</span>
    </span>
  );
}

function AlertStatusBadge({ status }: { status: string }) {
  const variant: "default" | "secondary" | "destructive" | "outline" =
    status === "sent"
      ? "default"
      : status === "dlq" || status === "failed" || status === "bounced"
        ? "destructive"
        : status === "pending"
          ? "secondary"
          : "outline";
  const label =
    status === "sent"
      ? "Sendt"
      : status === "pending"
        ? "Afventer"
        : status === "dlq"
          ? "Fejlet (DLQ)"
          : status === "suppressed"
            ? "Undertrykt"
            : status;
  return (
    <Badge variant={variant} className="text-xs">
      {label}
    </Badge>
  );
}

/**
 * Computes ms until the next scheduled cron run.
 * Schedule (UTC, must match pg_cron jobs):
 *  - delta: '15 * * * *'  (every hour at xx:15 UTC)
 *  - full:  '0 3 * * *'   (daily at 03:00 UTC)
 */
function getNextCronRun(now: Date): { type: "delta" | "full"; at: Date } {
  const nextDelta = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    now.getUTCHours(), 15, 0, 0,
  ));
  if (nextDelta.getTime() <= now.getTime()) {
    nextDelta.setUTCHours(nextDelta.getUTCHours() + 1);
  }
  const nextFull = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 3, 0, 0, 0,
  ));
  if (nextFull.getTime() <= now.getTime()) {
    nextFull.setUTCDate(nextFull.getUTCDate() + 1);
  }
  return nextFull.getTime() < nextDelta.getTime()
    ? { type: "full", at: nextFull }
    : { type: "delta", at: nextDelta };
}

function NextCronBadge() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const next = useMemo(() => getNextCronRun(now), [now]);
  const diffMs = next.at.getTime() - now.getTime();
  const totalMins = Math.max(0, Math.round(diffMs / 60_000));
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const timeLabel =
    totalMins < 1
      ? "om mindre end et minut"
      : hours > 0
        ? `om ${hours} t ${mins} min`
        : `om ${mins} min`;
  const localTime = next.at.toLocaleTimeString("da-DK", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const typeLabel = next.type === "delta" ? "Delta-cron" : "Fuld sync";
  return (
    <Badge
      variant="outline"
      className="mt-2 inline-flex items-center gap-1.5 font-normal"
      title={`Næste planlagte kørsel: ${next.at.toUTCString()}`}
    >
      <Clock size={12} className="opacity-70" />
      <span>
        {typeLabel} {timeLabel}{" "}
        <span className="text-muted-foreground">· kl. {localTime}</span>
      </span>
    </Badge>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Lige nu";
  if (mins < 60) return `${mins} min siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} t siden`;
  return `${Math.floor(hours / 24)} d siden`;
}

function ImageHealthCard() {
  const queryClient = useQueryClient();
  const fetchReport = useServerFn(getLatestImageHealthReport);
  const triggerCheck = useServerFn(triggerImageHealthCheck);
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);

  // Filter UI state for the failure table — admins can quickly narrow down to
  // a specific brand/model/carId/bilinfoId or to a specific HTTP status code
  // (e.g. show only 404s when CDN purge is being investigated).
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [kindFilter, setKindFilter] = useState<"all" | "thumb" | "original">("all");

  // Local progress state — we don't get streaming progress from the server,
  // so we simulate a smooth 0→90% climb while the request is in-flight and
  // jump to 100% on success. Also tracks elapsed time for live UX feedback.
  const [progress, setProgress] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [lastSummary, setLastSummary] = useState<{
    totalCarsChecked: number;
    totalUrlsChecked: number;
    failedUrlsCount: number;
    carsWithFailures: number;
    durationMs: number;
    finishedAt: string;
  } | null>(null);

  const { data: report, isLoading } = useQuery<ImageHealthReport | null>({
    queryKey: ["image-health-report"],
    queryFn: () => fetchReport(),
    refetchInterval: 60_000,
  });

  const triggerMutation = useMutation({
    mutationFn: () => triggerCheck(),
    onMutate: () => {
      setProgress(2);
      setElapsedMs(0);
      setLastSummary(null);
    },
    onSuccess: (res) => {
      setProgress(100);
      setLastSummary({
        totalCarsChecked: res.totalCarsChecked,
        totalUrlsChecked: res.totalUrlsChecked,
        failedUrlsCount: res.failedUrlsCount,
        carsWithFailures: res.carsWithFailures,
        durationMs: res.durationMs ?? elapsedMs,
        finishedAt: new Date().toISOString(),
      });
      const failed = res.failedUrlsCount;
      const cars = res.carsWithFailures;
      if (failed === 0) {
        toast.success(`Image health OK — ${res.totalUrlsChecked} URL'er tjekket på ${res.totalCarsChecked} biler`);
      } else {
        toast.warning(`${failed} URL'er fejlede på ${cars} bil${cars === 1 ? "" : "er"}`);
      }
      queryClient.invalidateQueries({ queryKey: ["image-health-report"] });
    },
    onError: (e) => {
      setProgress(0);
      toast.error(e instanceof Error ? e.message : "Kunne ikke køre image health check");
    },
  });

  // Drive the simulated progress + elapsed timer while the mutation is pending.
  useEffect(() => {
    if (!triggerMutation.isPending) return;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      setElapsedMs(elapsed);
      // Asymptotic curve toward 90% — fast at first, slows down.
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const next = prev + Math.max(0.5, (92 - prev) * 0.04);
        return Math.min(90, next);
      });
    }, 200);
    return () => clearInterval(id);
  }, [triggerMutation.isPending]);

  // Group failures by car so we can render one row per car with all failing URLs.
  const grouped = useMemo(() => {
    if (!report) return [];
    const map = new Map<string, ImageHealthReport["failures"]>();
    for (const f of report.failures) {
      const arr = map.get(f.carId) ?? [];
      arr.push(f);
      map.set(f.carId, arr);
    }
    return Array.from(map.entries()).map(([carId, failures]) => ({ carId, failures }));
  }, [report]);

  // Distinct HTTP status codes present in the latest report — used to populate
  // the status filter dropdown so admins only see options that actually exist.
  // "ERR" is a synthetic bucket for failures with no http status (network errors).
  const availableStatuses = useMemo(() => {
    if (!report) return [] as string[];
    const set = new Set<string>();
    for (const f of report.failures) {
      set.add(f.httpStatus == null ? "ERR" : String(f.httpStatus));
    }
    return Array.from(set).sort((a, b) => {
      if (a === "ERR") return 1;
      if (b === "ERR") return -1;
      return Number(a) - Number(b);
    });
  }, [report]);

  // Apply search + status + kind filters. Search matches carId, bilinfoId,
  // brand and model (case-insensitive). Status filter narrows individual
  // failures to a specific HTTP code, and the car row drops out if no
  // failures match. Kind filter narrows thumb vs original.
  const filteredGrouped = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return grouped
      .map(({ carId, failures }) => {
        const visibleFailures = failures.filter((f) => {
          if (statusFilter !== "all") {
            const code = f.httpStatus == null ? "ERR" : String(f.httpStatus);
            if (code !== statusFilter) return false;
          }
          if (kindFilter !== "all" && f.kind !== kindFilter) return false;
          return true;
        });
        return { carId, failures: visibleFailures };
      })
      .filter(({ carId, failures }) => {
        if (failures.length === 0) return false;
        if (!q) return true;
        const info = report?.carInfo[carId];
        const haystack = [
          carId,
          failures[0]?.bilinfoId ?? "",
          info?.brand ?? "",
          info?.model ?? "",
          info ? `${info.brand} ${info.model}` : "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [grouped, report, searchQuery, statusFilter, kindFilter]);

  const totalFilteredFailures = useMemo(
    () => filteredGrouped.reduce((acc, g) => acc + g.failures.length, 0),
    [filteredGrouped],
  );
  const filtersActive =
    searchQuery.trim().length > 0 || statusFilter !== "all" || kindFilter !== "all";

  const isPending = triggerMutation.isPending;

  return (
    <div className="mt-8 rounded-xl bg-card ring-1 ring-border">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-6">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <ImageOff size={18} className="text-muted-foreground" />
            Billede-sundhed (thumbnails &amp; originaler)
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Kører automatisk efter hver Bilinfo-sync samt nattens cron (kl. 02:30 dansk tid).{" "}
            {report?.createdAt && (
              <>
                Sidst kørt {formatRelative(report.createdAt)} ·{" "}
                <span className="font-mono text-[10px]">{report.triggeredBy}</span>
              </>
            )}
          </p>
          {report?.lastCronRun && (
            <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-md bg-muted/60 px-2.5 py-1 text-[11px] text-muted-foreground ring-1 ring-border">
              <Clock size={11} className="text-muted-foreground" />
              <span>Seneste automatiske kørsel:</span>
              <span className="font-medium text-foreground">
                {formatRelative(report.lastCronRun.createdAt)}
              </span>
              <span className="font-mono text-[10px]">{report.lastCronRun.triggeredBy}</span>
              {report.lastCronRun.durationMs != null && (
                <span className="tabular-nums">
                  · {(report.lastCronRun.durationMs / 1000).toFixed(1)}s
                </span>
              )}
              <span
                className={
                  report.lastCronRun.failedUrlsCount > 0
                    ? "text-destructive"
                    : "text-emerald-600 dark:text-emerald-400"
                }
              >
                · {report.lastCronRun.failedUrlsCount === 0
                  ? "ingen fejl"
                  : `${report.lastCronRun.failedUrlsCount} fejl på ${report.lastCronRun.carsWithFailures} bil${report.lastCronRun.carsWithFailures === 1 ? "" : "er"}`}
              </span>
            </div>
          )}
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => triggerMutation.mutate()}
          disabled={isPending}
          className="gap-2"
        >
          {isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <PlayCircle size={14} />
          )}
          {isPending ? "Kører check..." : "Kør on-demand check"}
        </Button>
      </div>

      {/* Live progress while running */}
      {isPending && (
        <div className="border-b border-border bg-muted/30 p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Loader2 size={14} className="animate-spin text-primary" />
              Kører image health check...
            </div>
            <div className="flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
              <Timer size={12} />
              {(elapsedMs / 1000).toFixed(1)}s forløbet
            </div>
          </div>
          <Progress value={progress} className="mt-3 h-2" />
          <p className="mt-2 text-xs text-muted-foreground">
            Probe'r alle aktive Bilinfo-biler — dette kan tage op til et minut afhængigt af flådestørrelsen.
          </p>
        </div>
      )}

      {/* Summary panel after a successful on-demand run */}
      {!isPending && lastSummary && (
        <div
          className={`relative border-b border-border p-6 ${
            lastSummary.failedUrlsCount > 0
              ? "bg-destructive/5"
              : "bg-emerald-50 dark:bg-emerald-950/20"
          }`}
        >
          <button
            type="button"
            onClick={() => setLastSummary(null)}
            className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
            aria-label="Luk opsummering"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2">
            {lastSummary.failedUrlsCount === 0 ? (
              <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertCircle size={18} className="text-destructive" />
            )}
            <h3 className="text-sm font-semibold text-foreground">
              {lastSummary.failedUrlsCount === 0
                ? "Check færdig — alle billeder OK"
                : `Check færdig — ${lastSummary.failedUrlsCount} fejl fundet`}
            </h3>
            <span className="ml-auto text-xs text-muted-foreground">
              {formatRelative(lastSummary.finishedAt)}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryTile label="Biler tjekket" value={lastSummary.totalCarsChecked.toString()} />
            <SummaryTile label="URL'er tjekket" value={lastSummary.totalUrlsChecked.toString()} />
            <SummaryTile
              label="Fejlende URL'er"
              value={lastSummary.failedUrlsCount.toString()}
              tone={lastSummary.failedUrlsCount > 0 ? "destructive" : "success"}
            />
            <SummaryTile
              label="Varighed"
              value={`${(lastSummary.durationMs / 1000).toFixed(1)}s`}
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Indlæser rapport...</div>
      ) : !report ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          Ingen rapport endnu. Kør et check eller vent til næste Bilinfo-sync.
        </div>
      ) : (
        <>
          <div className="grid gap-4 border-b border-border p-6 sm:grid-cols-4">
            <ImageHealthStat label="Biler tjekket" value={report.totalCarsChecked} />
            <ImageHealthStat label="URL'er tjekket" value={report.totalUrlsChecked} />
            <ImageHealthStat
              label="Fejlende URL'er"
              value={report.failedUrlsCount}
              tone={report.failedUrlsCount > 0 ? "destructive" : "success"}
            />
            <ImageHealthStat
              label="Biler med fejl"
              value={report.carsWithFailures}
              tone={report.carsWithFailures > 0 ? "destructive" : "success"}
            />
          </div>

          {grouped.length === 0 ? (
            <div className="flex items-center gap-2 p-6 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={16} />
              Alle prøvede billeder svarer korrekt.
            </div>
          ) : (
            <>
              {/* Filter bar — sort/search through failures by carId, brand,
                  model, bilinfoId, HTTP status (404/403/...) eller billede-type. */}
              <div className="flex flex-wrap items-end gap-3 border-b border-border bg-muted/20 p-4">
                <div className="min-w-[220px] flex-1">
                  <Label htmlFor="img-health-search" className="mb-1.5 block text-xs text-muted-foreground">
                    Søg
                  </Label>
                  <div className="relative">
                    <Search
                      size={14}
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      id="img-health-search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="carId, brand, model eller bilinfo-id..."
                      className="h-9 pl-8"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        aria-label="Ryd søgning"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-40">
                  <Label className="mb-1.5 block text-xs text-muted-foreground">HTTP status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle ({report.failedUrlsCount})</SelectItem>
                      {availableStatuses.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s === "ERR" ? "Netværksfejl" : s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-36">
                  <Label className="mb-1.5 block text-xs text-muted-foreground">Type</Label>
                  <Select
                    value={kindFilter}
                    onValueChange={(v) => setKindFilter(v as "all" | "thumb" | "original")}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      <SelectItem value="thumb">Thumb</SelectItem>
                      <SelectItem value="original">Original</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="ml-auto flex items-center gap-2 pb-1 text-xs text-muted-foreground">
                  <Filter size={12} />
                  <span className="tabular-nums">
                    {filteredGrouped.length} bil{filteredGrouped.length === 1 ? "" : "er"} ·{" "}
                    {totalFilteredFailures} URL'er
                  </span>
                  {filtersActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        setSearchQuery("");
                        setStatusFilter("all");
                        setKindFilter("all");
                      }}
                    >
                      Nulstil
                    </Button>
                  )}
                </div>
              </div>

              {filteredGrouped.length === 0 ? (
                <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                  <Search size={14} />
                  Ingen fejl matcher de valgte filtre.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bil</TableHead>
                      <TableHead>Bilinfo-ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Fejlende URL'er</TableHead>
                      <TableHead className="w-[100px] text-right">Inspect</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGrouped.map(({ carId, failures }) => {
                      const info = report.carInfo[carId];
                      const bilinfoId = failures[0]?.bilinfoId ?? null;
                      const carLabel = info ? `${info.brand} ${info.model}` : carId.slice(0, 8);
                      return (
                        <TableRow key={carId}>
                          <TableCell className="text-sm">
                            <div className="flex flex-col gap-1">
                              {info?.seoSlug ? (
                                <Link
                                  to="/biler-til-salg/$slug"
                                  params={{ slug: info.seoSlug }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-2 hover:underline"
                                  title="Åbn offentlig bilside i nyt vindue"
                                >
                                  <Highlight text={carLabel} query={searchQuery} />
                                  <ExternalLink size={12} />
                                </Link>
                              ) : (
                                <span className="font-medium text-muted-foreground">
                                  <Highlight text={carLabel} query={searchQuery} />
                                </span>
                              )}
                          <Link
                            to="/admin/biler"
                            search={{ editId: carId } as never}
                            className="inline-flex w-fit items-center gap-1 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] font-medium text-foreground transition hover:border-accent hover:bg-accent/10 hover:text-accent"
                            title="Åbn bilen i admin og rediger felter"
                          >
                            <Settings2 size={11} />
                            Rediger i admin
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell>
                        {bilinfoId ? (
                          <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{bilinfoId}</code>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {info ? (
                          <Badge variant={info.status === "active" ? "outline" : "secondary"} className="text-[10px]">
                            {info.status}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            slettet
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <ul className="space-y-1">
                          {failures.map((f, idx) => (
                            <li key={`${f.url}-${idx}`} className="flex items-start gap-2 text-xs">
                              <Badge
                                variant={f.kind === "thumb" ? "secondary" : "outline"}
                                className="mt-0.5 shrink-0 text-[10px] uppercase"
                              >
                                {f.kind}
                              </Badge>
                              <Badge variant="destructive" className="mt-0.5 shrink-0 text-[10px]">
                                {f.httpStatus ?? "ERR"}
                              </Badge>
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="break-all text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                title={f.errorMessage ?? undefined}
                              >
                                {f.url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedCarId(carId)}
                          className="gap-1.5"
                        >
                          <Search size={12} />
                          Detaljer
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
              )}
            </>
          )}
        </>
      )}

      <CarImageProbeDialog
        carId={selectedCarId}
        carInfoFromReport={selectedCarId ? report?.carInfo[selectedCarId] : undefined}
        onClose={() => setSelectedCarId(null)}
      />
    </div>
  );
}

// Module-level cache for per-car probe results. Lives outside React Query so
// it survives even when the dialog unmounts and React Query GCs the entry —
// admins frequently close/re-open the modal to compare cars, and re-probing
// every URL takes seconds. TTL is short (2 min) so fixes are reflected
// quickly; "Probe igen" always bypasses.
const PROBE_CACHE_TTL_MS = 2 * 60 * 1000;
const probeCache = new Map<string, { data: CarImageProbeReport; fetchedAt: number }>();

function getCachedProbe(carId: string): { data: CarImageProbeReport; expiresAt: number } | null {
  const entry = probeCache.get(carId);
  if (!entry) return null;
  const expiresAt = entry.fetchedAt + PROBE_CACHE_TTL_MS;
  if (expiresAt <= Date.now()) {
    probeCache.delete(carId);
    return null;
  }
  return { data: entry.data, expiresAt };
}

function CarImageProbeDialog({
  carId,
  carInfoFromReport,
  onClose,
}: {
  carId: string | null;
  carInfoFromReport?: ImageHealthReport["carInfo"][string];
  onClose: () => void;
}) {
  const probe = useServerFn(probeSingleCarImages);
  const queryClient = useQueryClient();

  // Track when the currently-shown data was originally fetched so we can
  // render an "udløber om Xs" countdown and stop auto-probing while fresh.
  const [cacheExpiresAt, setCacheExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const { data, isLoading, isFetching, refetch, error } = useQuery<CarImageProbeReport>({
    queryKey: ["car-image-probe", carId],
    queryFn: async () => {
      const result = await probe({ data: { carId: carId! } });
      probeCache.set(carId!, { data: result, fetchedAt: Date.now() });
      setCacheExpiresAt(Date.now() + PROBE_CACHE_TTL_MS);
      return result;
    },
    enabled: false,
    retry: false,
    staleTime: PROBE_CACHE_TTL_MS,
    initialData: () => (carId ? getCachedProbe(carId)?.data : undefined),
  });

  // Auto-probe only when we don't already have a fresh cached result. If a
  // cached entry exists, just surface its expiry — admin can hit "Probe igen"
  // to refresh on demand.
  useEffect(() => {
    if (!carId) {
      setCacheExpiresAt(null);
      return;
    }
    const cached = getCachedProbe(carId);
    if (cached) {
      setCacheExpiresAt(cached.expiresAt);
    } else {
      setCacheExpiresAt(null);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  // Tick a 1Hz clock while a cached result is shown so the countdown updates.
  useEffect(() => {
    if (cacheExpiresAt == null) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cacheExpiresAt]);

  const cacheRemainingMs =
    cacheExpiresAt != null ? Math.max(0, cacheExpiresAt - now) : 0;
  const cacheRemainingSec = Math.ceil(cacheRemainingMs / 1000);
  const isCacheFresh = cacheExpiresAt != null && cacheRemainingMs > 0;

  const reRun = async () => {
    try {
      // Force a fresh probe — drop the module cache entry first so the
      // initialData accessor doesn't briefly show stale data on re-render.
      if (carId) probeCache.delete(carId);
      setCacheExpiresAt(null);
      await refetch();
      // Refresh the parent report too so badges line up after fixing.
      queryClient.invalidateQueries({ queryKey: ["image-health-report"] });
      toast.success("Bilens billeder er probet på ny");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Probe fejlede");
    }
  };

  const open = carId !== null;
  const fallbackLabel = carInfoFromReport
    ? `${carInfoFromReport.brand} ${carInfoFromReport.model}`
    : "Bil";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageOff size={18} />
            {data ? `${data.brand} ${data.model}` : fallbackLabel}
            {data?.status && (
              <Badge variant={data.status === "active" ? "outline" : "secondary"} className="text-[10px]">
                {data.status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            On-demand probe af alle billeder for denne bil (thumb + original).{" "}
            {data?.bilinfoId && (
              <>
                Bilinfo-ID: <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{data.bilinfoId}</code>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3 border-y border-border py-3">
          {data ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span>
                <span className="font-medium text-foreground">{data.totalImages}</span> billeder ·{" "}
                <span className="font-medium text-foreground">{data.totalUrlsChecked}</span> URL'er tjekket ·{" "}
                <span className={data.failedUrlsCount > 0 ? "font-semibold text-destructive" : "font-semibold text-emerald-600 dark:text-emerald-400"}>
                  {data.failedUrlsCount} fejl
                </span>
                {data.durationMs != null && (
                  <> · {(data.durationMs / 1000).toFixed(1)}s</>
                )}
              </span>
              {isCacheFresh && !isFetching && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                  title={`Resultatet er cachet i ${Math.round(PROBE_CACHE_TTL_MS / 1000)} sekunder. Tryk "Probe igen" for et frisk tjek.`}
                >
                  <Clock size={10} />
                  Cached · udløber om{" "}
                  <span className="tabular-nums text-foreground">{cacheRemainingSec}s</span>
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Tjekker billeder...</div>
          )}
          <div className="flex gap-2">
            {data?.seoSlug && (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link
                  to="/biler-til-salg/$slug"
                  params={{ slug: data.seoSlug }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={12} />
                  Åbn bil
                </Link>
              </Button>
            )}
            {carId && (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link
                  to="/admin/biler"
                  search={{ editId: carId } as never}
                >
                  <Settings2 size={12} />
                  Rediger i admin
                </Link>
              </Button>
            )}
            <Button
              size="sm"
              onClick={reRun}
              disabled={isFetching}
              className="gap-1.5"
            >
              <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} />
              {isFetching ? "Probing..." : "Probe igen"}
            </Button>
          </div>
        </div>

        {error ? (
          <div className="rounded bg-destructive/10 p-3 text-sm text-destructive">
            {error instanceof Error ? error.message : "Probe fejlede"}
          </div>
        ) : isLoading || (!data && isFetching) ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Probing...</div>
        ) : !data ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Klik "Probe igen" for at starte.</div>
        ) : data.totalImages === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Bilen har ingen billeder i databasen.
          </div>
        ) : (
          <div className="space-y-2">
            {data.rows.map((row) => {
              const orig = row.originalStatus;
              const thumb = row.thumbStatus;
              const anyFail = !orig.ok || (thumb !== null && !thumb.ok);
              return (
                <div
                  key={row.index}
                  className={`rounded-md border p-3 text-xs ${
                    anyFail ? "border-destructive/40 bg-destructive/5" : "border-border bg-muted/30"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      #{row.index + 1}
                    </span>
                    {anyFail ? (
                      <Badge variant="destructive" className="text-[10px]">
                        <AlertCircle size={10} className="mr-0.5" />
                        Fejl
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={10} className="mr-0.5" />
                        OK
                      </Badge>
                    )}
                  </div>

                  <ProbeUrlRow kind="original" url={row.originalUrl} status={orig} />
                  {thumb && row.thumbUrl && (
                    <ProbeUrlRow kind="thumb" url={row.thumbUrl} status={thumb} />
                  )}
                  {!thumb && (
                    <div className="mt-1 text-[11px] italic text-muted-foreground">
                      Ingen thumbnail-variant (ikke en .webp i car-images-bucketen)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProbeUrlRow({
  kind,
  url,
  status,
}: {
  kind: "thumb" | "original";
  url: string;
  status:
    | { ok: true }
    | { ok: false; httpStatus: number | null; errorMessage: string };
}) {
  return (
    <div className="mt-1 flex items-start gap-2">
      <Badge
        variant={kind === "thumb" ? "secondary" : "outline"}
        className="mt-0.5 shrink-0 text-[10px] uppercase"
      >
        {kind}
      </Badge>
      {status.ok ? (
        <Badge variant="outline" className="mt-0.5 shrink-0 text-[10px] border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
          200
        </Badge>
      ) : (
        <Badge variant="destructive" className="mt-0.5 shrink-0 text-[10px]">
          {status.httpStatus ?? "ERR"}
        </Badge>
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        title={status.ok ? undefined : status.errorMessage}
      >
        {url}
      </a>
    </div>
  );
}

function ImageHealthStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "destructive" | "success";
}) {
  const valueColor =
    tone === "destructive" && value > 0
      ? "text-destructive"
      : tone === "success" && value === 0
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-foreground";
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "destructive" | "success";
}) {
  const valueColor =
    tone === "destructive"
      ? "text-destructive"
      : tone === "success"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-foreground";
  return (
    <div className="rounded-lg bg-background/60 p-3 ring-1 ring-border">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
    </div>
  );
}

const MiniStat = SummaryTile;

// ─────────────────────────────────────────────────────────────────────────────
// Re-import ALL images per car from Bilinfo.
//
// The server function processes one car at a time so each request stays under
// the Worker timeout. The component fetches the list of cars once, then loops
// sequentially with a live progress bar. Concurrency = 2 to keep storage
// rate-limits and Bilinfo origin happy.
// ─────────────────────────────────────────────────────────────────────────────

type ReimportResult = {
  carId: string;
  bilinfoId: string;
  brand?: string;
  model?: string;
  total: number;
  downloaded: number;
  reused: number;
  failed: number;
  error?: string;
};

function ReimportAllImagesCard() {
  const listCars = useServerFn(listBilinfoCarIdsForReimport);
  const reimportOne = useServerFn(reimportCarImages);

  const [running, setRunning] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<ReimportResult[]>([]);
  const [current, setCurrent] = useState<string | null>(null);

  const totals = useMemo(() => {
    return results.reduce(
      (acc, r) => ({
        cars: acc.cars + 1,
        images: acc.images + r.total,
        downloaded: acc.downloaded + r.downloaded,
        reused: acc.reused + r.reused,
        failed: acc.failed + r.failed,
        errored: acc.errored + (r.error ? 1 : 0),
      }),
      { cars: 0, images: 0, downloaded: 0, reused: 0, failed: 0, errored: 0 },
    );
  }, [results]);

  const start = async () => {
    setRunning(true);
    setCancelRequested(false);
    setResults([]);
    setProgress({ done: 0, total: 0 });
    setCurrent(null);

    let list: Array<{ id: string; bilinfoId: string; brand: string; model: string }> = [];
    try {
      const res = await listCars();
      list = res.cars;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunne ikke hente bil-listen");
      setRunning(false);
      return;
    }

    if (list.length === 0) {
      toast.warning("Ingen Bilinfo-biler at gen-importere");
      setRunning(false);
      return;
    }

    setProgress({ done: 0, total: list.length });
    toast.info(`Starter gen-import af alle billeder for ${list.length} biler`);

    for (let i = 0; i < list.length; i++) {
      if (cancelRequested) break;
      const car = list[i];
      setCurrent(`${car.brand} ${car.model} (#${car.bilinfoId})`);
      try {
        const r = await reimportOne({ data: { carId: car.id } });
        setResults((prev) => [
          ...prev,
          {
            carId: r.carId,
            bilinfoId: r.bilinfoId,
            brand: r.brand ?? car.brand,
            model: r.model ?? car.model,
            total: r.total,
            downloaded: r.downloaded,
            reused: r.reused,
            failed: r.failed,
          },
        ]);
      } catch (e) {
        setResults((prev) => [
          ...prev,
          {
            carId: car.id,
            bilinfoId: car.bilinfoId,
            brand: car.brand,
            model: car.model,
            total: 0,
            downloaded: 0,
            reused: 0,
            failed: 0,
            error: e instanceof Error ? e.message : String(e),
          },
        ]);
      }
      setProgress({ done: i + 1, total: list.length });
    }

    setCurrent(null);
    setRunning(false);
    if (cancelRequested) {
      toast.warning(`Stoppet — ${progress.done}/${list.length} biler behandlet`);
    } else {
      toast.success(`Gen-import færdig: ${list.length} biler`);
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="mt-8 rounded-xl bg-card ring-1 ring-border">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-6">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <ImageIcon size={18} /> Gen-import af alle billeder
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Henter samtlige billeder (ikke kun thumbnail) for hver Bilinfo-bil og
            uploader dem til vores eget storage. Genbruger eksisterende filer
            når kilde-URL'en er uændret. Kan tage flere minutter ved mange biler.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancelRequested(true)}
              disabled={cancelRequested}
            >
              <X size={14} className="mr-1" />
              {cancelRequested ? "Stopper..." : "Stop"}
            </Button>
          ) : (
            <Button size="sm" onClick={start}>
              <ImageIcon size={14} className="mr-1" />
              Start gen-import af alle biler
            </Button>
          )}
        </div>
      </div>

      {(running || results.length > 0) && (
        <div className="p-6 space-y-4">
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>
                {progress.done} / {progress.total} biler{" "}
                {current && <span className="ml-2 text-foreground">· {current}</span>}
              </span>
              <span className="tabular-nums">{pct}%</span>
            </div>
            <Progress value={pct} />
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <MiniStat label="Biler" value={String(totals.cars)} />
            <MiniStat label="Billeder i alt" value={String(totals.images)} />
            <MiniStat label="Hentet" value={String(totals.downloaded)} tone="success" />
            <MiniStat label="Genbrugt" value={String(totals.reused)} />
            <MiniStat
              label="Fejlede"
              value={String(totals.failed + totals.errored)}
              tone={totals.failed + totals.errored > 0 ? "destructive" : undefined}
            />
          </div>

          {results.length > 0 && (
            <div className="max-h-72 overflow-y-auto rounded-lg ring-1 ring-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/60 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Bil</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                    <th className="px-3 py-2 font-medium text-right">Hentet</th>
                    <th className="px-3 py-2 font-medium text-right">Genbrugt</th>
                    <th className="px-3 py-2 font-medium text-right">Fejlet</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.slice().reverse().map((r) => (
                    <tr key={r.carId} className="border-t border-border">
                      <td className="px-3 py-1.5">
                        <span className="font-medium">{r.brand} {r.model}</span>
                        <span className="ml-2 text-muted-foreground font-mono">#{r.bilinfoId}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.total}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600">
                        {r.downloaded}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.reused}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.failed > 0 ? (
                          <span className="text-destructive">{r.failed}</span>
                        ) : (
                          "0"
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.error ? (
                          <span className="text-destructive" title={r.error}>
                            {r.error.length > 40 ? r.error.slice(0, 40) + "…" : r.error}
                          </span>
                        ) : r.failed > 0 ? (
                          <span className="text-amber-600">Delvis</span>
                        ) : (
                          <span className="text-emerald-600">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
